import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { X } from 'lucide-react';

export const RequestItineraryForm = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dates: '',
    interests: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Save to localStorage (frontend-only for MVP)
      const submissions = JSON.parse(localStorage.getItem('itineraryRequests') || '[]');
      const submission = {
        ...formData,
        timestamp: new Date().toISOString(),
        emailSentTo: 'contact@pomtrips.com',
        status: 'pending'
      };
      submissions.push(submission);
      localStorage.setItem('itineraryRequests', JSON.stringify(submissions));

      // Log for verification (in production, this sends actual email)
      console.log('📧 Email would be sent to contact@pomtrips.com:', submission);

      // Show success message
      toast({
        title: "Thanks! We received your request.",
        description: "We reply within 24 hours. Meanwhile, chat with us on WhatsApp.",
        duration: 6000,
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        dates: '',
        interests: '',
        message: ''
      });
      
      setIsSubmitting(false);
      
      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try WhatsApp instead.",
        variant: "destructive"
      });
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl text-[#2C2C2C]">
            {t('formTitle')}
          </DialogTitle>
          <DialogDescription className="text-[#8B7D6B] sr-only">
            Fill out the form below to request a custom travel itinerary
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-[#2C2C2C]">{t('formName')}</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="border-[#D4C4B0] focus:border-[#C4B5A0] focus:ring-[#C4B5A0]"
            />
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-[#2C2C2C]">{t('formEmail')}</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="border-[#D4C4B0] focus:border-[#C4B5A0] focus:ring-[#C4B5A0]"
            />
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-[#2C2C2C]">{t('formPhone')}</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              required
              className="border-[#D4C4B0] focus:border-[#C4B5A0] focus:ring-[#C4B5A0]"
            />
          </div>

          {/* Travel Dates */}
          <div className="space-y-2">
            <Label htmlFor="dates" className="text-[#2C2C2C]">{t('formDates')}</Label>
            <Input
              id="dates"
              name="dates"
              type="text"
              placeholder="e.g., March 15-25, 2025"
              value={formData.dates}
              onChange={handleChange}
              className="border-[#D4C4B0] focus:border-[#C4B5A0] focus:ring-[#C4B5A0]"
            />
          </div>

          {/* Interests */}
          <div className="space-y-2">
            <Label htmlFor="interests" className="text-[#2C2C2C]">{t('formInterests')}</Label>
            <Input
              id="interests"
              name="interests"
              type="text"
              placeholder="e.g., Yoga, Heritage, Wildlife"
              value={formData.interests}
              onChange={handleChange}
              className="border-[#D4C4B0] focus:border-[#C4B5A0] focus:ring-[#C4B5A0]"
            />
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="message" className="text-[#2C2C2C]">{t('formMessage')}</Label>
            <Textarea
              id="message"
              name="message"
              rows={4}
              value={formData.message}
              onChange={handleChange}
              className="border-[#D4C4B0] focus:border-[#C4B5A0] focus:ring-[#C4B5A0]"
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#C4B5A0] hover:bg-[#6B4E37] text-white py-6 text-lg"
          >
            {isSubmitting ? 'Sending...' : t('formSubmit')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};